// Contracts — vista TASK CATALOGUE: the topics the client buys, as the
// contributor picks them before filming.
//
// The catalogue is data, not an enum, because the client revises it between
// waves — but the split itself is theirs and is preserved verbatim: `focus`
// topics are actively bought, `deprioritised` ones they explicitly told us they
// do not want. Deprioritised rows stay in the table rather than being deleted:
// a contributor who asks "can I film cycling?" deserves a straight no, not a
// silent absence.
import { z } from 'zod';
import { VistaTaskPrioritySchema } from './enums';

export const VistaTaskSchema = z.object({
  slug: z.string(),
  category: z.string(),
  // Titles are per-language columns rather than a translations table: the list
  // is a hundred rows in four languages and will never justify the join.
  titleEn: z.string(),
  titleEs: z.string().nullable(),
  titlePt: z.string().nullable(),
  titleRu: z.string().nullable(),
  priority: VistaTaskPrioritySchema,
  active: z.boolean(),
});
export type VistaTask = z.infer<typeof VistaTaskSchema>;

// GET /api/vista/tasks → what this person may film, in their language.
export const VistaTasksResSchema = z.object({
  rows: z.array(VistaTaskSchema),
});
export type VistaTasksRes = z.infer<typeof VistaTasksResSchema>;

// POST /api/vista/_admin/tasks — seed or revise the catalogue (machine gate).
// Upsert by slug: re-running a wave's list must be a no-op, not a duplicate.
export const VistaTasksUpsertReqSchema = z.object({
  rows: z
    .array(
      z.object({
        slug: z.string().trim().min(1).max(80),
        category: z.string().trim().min(1).max(80),
        titleEn: z.string().trim().min(1).max(200),
        titleEs: z.string().trim().max(200).nullable().optional(),
        titlePt: z.string().trim().max(200).nullable().optional(),
        titleRu: z.string().trim().max(200).nullable().optional(),
        priority: VistaTaskPrioritySchema.optional(),
        active: z.boolean().optional(),
        sortOrder: z.number().int().min(0).max(10_000).optional(),
      }),
    )
    .min(1)
    .max(300),
});
export type VistaTasksUpsertReq = z.infer<typeof VistaTasksUpsertReqSchema>;

export const VistaTasksUpsertResSchema = z.object({
  ok: z.literal(true),
  written: z.number().int(),
});
export type VistaTasksUpsertRes = z.infer<typeof VistaTasksUpsertResSchema>;
